/**
 * Pure rule-matching logic extracted from chartDataFunctions.tsx so it can be
 * used on the server (budget loader) without pulling in chart/DOM dependencies.
 *
 * The cashflow chart code re-exports these via chartDataFunctions; the budget
 * server code imports them directly from here.
 */

import {
  transactionRuleType,
  transacitonActionType,
  transactionRuleFields,
} from "~/components/charts/cashflowChart/cashflowEnums";
import type { TransactionRule } from "~/types/global.types";
import { parseTransactionEditRulePayload } from "~/utils/transactionEditRule";

export interface RuleMatchableTransaction {
  id?: string;
  description?: { description?: string; additionalInfo?: string };
  transactionDescription?: string;
  amount?: { chargedAmount?: { amount?: number | string } };
  accountNumber?: string;
  date?: { valueDate?: string };
  checksum?: string;
  details?: string;
}

function getTransactionFieldFromRule(
  transaction: RuleMatchableTransaction,
  ruleField: string
): string | undefined {
  switch (ruleField) {
    case transactionRuleFields.ID:
      return transaction.id;
    case transactionRuleFields.NAME:
      return transaction.description?.description;
    case transactionRuleFields.EXTRA_INFO:
      return transaction.transactionDescription;
    case transactionRuleFields.CHECKSUM:
      return transaction.checksum;
  }
}

/** Account number from the Open Finance `additionalInfo` JSON, if present. */
function getAdditionalInfoAccountNo(
  transaction: RuleMatchableTransaction
): string | undefined {
  const raw = transaction.description?.additionalInfo;
  if (!raw) return undefined;
  try {
    const accountNo = JSON.parse(raw)?.accountNo;
    if (typeof accountNo === "string" && accountNo) return accountNo;
  } catch {
    // not JSON
  }
  return undefined;
}

function doesSumMatch(
  transaction: RuleMatchableTransaction,
  sum: number | null
): boolean {
  if (!sum) return true;
  const amount = transaction.amount?.chargedAmount?.amount;
  if (amount == null) return false;
  return Math.abs(Math.round(parseFloat(String(amount)))) === sum;
}

export function transactionMatchesRule(
  rule: TransactionRule,
  transaction: RuleMatchableTransaction
): boolean {
  const field = getTransactionFieldFromRule(transaction, rule.rule_field);
  const sumOk = doesSumMatch(transaction, rule.sum ?? null);
  if (!sumOk) return false;

  if (rule.rule_type === transactionRuleType.EQUAL) {
    return field === rule.rule_value;
  }
  if (rule.rule_type === transactionRuleType.CONTAINS) {
    if (field?.includes(rule.rule_value)) return true;
    // "Extra info" rules should also match the destination account number
    // (shown to the user in the transaction's additional-info section).
    if (rule.rule_field === transactionRuleFields.EXTRA_INFO) {
      return !!getAdditionalInfoAccountNo(transaction)?.includes(
        rule.rule_value
      );
    }
    return false;
  }
  return false;
}

export function computeChecksum(transaction: RuleMatchableTransaction): string {
  const acc = transaction.accountNumber ?? "";
  const date = transaction.date?.valueDate ?? "";
  const amount = transaction.amount?.chargedAmount?.amount ?? "";
  const desc = transaction.description?.description ?? "";
  return `${acc}-${date}-${amount}-${desc}`;
}

export function getStableTransactionChecksum(
  transaction: RuleMatchableTransaction
): string {
  return transaction.checksum || computeChecksum(transaction);
}

function parseAdditionalInfo(transaction: RuleMatchableTransaction): void {
  if (transaction.description?.additionalInfo) {
    let parsed = transaction.description.additionalInfo;
    try {
      const obj = JSON.parse(transaction.description.additionalInfo);
      if (obj?.purposeDescription) parsed = obj.purposeDescription;
    } catch {
      // keep raw string
    }
    transaction.transactionDescription = parsed;
  } else if (
    transaction.details &&
    !transaction.details.includes("{")
  ) {
    transaction.transactionDescription = transaction.details;
  }
}

export interface AppliedRuleResult {
  ignored: boolean;
  isInvestment: boolean;
  autoDuplicate: boolean;
  effectiveCategory: string | null;
}

/**
 * Order rules so EDIT_TRANSACTION rules apply before everything else.
 *
 * An EDIT_TRANSACTION payload carries a snapshot of the category taken when
 * the edit was saved (the edit dialog cannot change the category). A matching
 * CATEGORY_CHANGE rule must always win over that stale snapshot regardless of
 * creation/fetch order — otherwise recategorizing an edited transaction has no
 * effect. Applying edit rules first keeps last-write-wins for everything else.
 */
export function orderRulesForApplication<T extends { action_type: string }>(
  rules: T[]
): T[] {
  const edits: T[] = [];
  const others: T[] = [];
  for (const rule of rules) {
    if (rule.action_type === transacitonActionType.EDIT_TRANSACTION) {
      edits.push(rule);
    } else {
      others.push(rule);
    }
  }
  return [...edits, ...others];
}

/**
 * Apply all matching rules to a transaction and return the aggregate result.
 * Does not mutate the transaction object.
 * NOTE: Does not detect auto-duplicates (cross-transaction concern).
 * Use batchApplyRulesForBudget for duplicate-aware results.
 */
export function applyRulesForBudget(
  rules: TransactionRule[],
  transaction: RuleMatchableTransaction
): AppliedRuleResult {
  const tx = { ...transaction };
  tx.checksum = tx.checksum || computeChecksum(tx);
  parseAdditionalInfo(tx);

  let ignored = false;
  let isInvestment = false;
  let categoryFromChange: string | null = null;
  let categoryFromEdit: string | null = null;

  for (const rule of rules) {
    if (!transactionMatchesRule(rule, tx)) continue;

    if (rule.action_type === transacitonActionType.IGNORE) {
      ignored = true;
    }
    if (rule.action_type === transacitonActionType.CATEGORY_CHANGE) {
      categoryFromChange = rule.action_value || null;
    }
    if (rule.action_type === transacitonActionType.EDIT_TRANSACTION) {
      categoryFromEdit =
        parseTransactionEditRulePayload(rule.action_value)?.subcategory_code ||
        categoryFromEdit;
    }
    if (rule.action_type === transacitonActionType.MARK_AS_INVESTMENT) {
      isInvestment = true;
    }
  }

  // A CATEGORY_CHANGE rule always beats the category snapshot baked into an
  // EDIT_TRANSACTION payload — the snapshot goes stale the moment the user
  // recategorizes the transaction (see orderRulesForApplication).
  const effectiveCategory = categoryFromChange ?? categoryFromEdit;

  return { ignored, isInvestment, autoDuplicate: false, effectiveCategory };
}

/**
 * Apply rules to a batch of transactions with auto-duplicate detection.
 * Returns an array of results aligned with the input transactions array.
 * Transactions sharing a checksum (beyond the first occurrence) are marked
 * as autoDuplicate unless a CONFIRM_DUPLICATE rule exists for that checksum.
 */
export function batchApplyRulesForBudget(
  rules: TransactionRule[],
  transactions: RuleMatchableTransaction[]
): AppliedRuleResult[] {
  const confirmedChecksums = new Set(
    rules
      .filter((r) => r.action_type === transacitonActionType.CONFIRM_DUPLICATE)
      .map((r) => r.rule_value)
  );

  const checksumCount = new Map<string, number>();
  const results: AppliedRuleResult[] = [];

  for (const transaction of transactions) {
    const result = applyRulesForBudget(rules, transaction);

    const checksum = transaction.checksum || computeChecksum(transaction);
    const seen = checksumCount.get(checksum) ?? 0;
    checksumCount.set(checksum, seen + 1);

    if (seen > 0 && !confirmedChecksums.has(checksum)) {
      result.autoDuplicate = true;
      result.ignored = true;
    }

    results.push(result);
  }

  return results;
}
